import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import Pagination from "@mui/material/Pagination";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import React, { useEffect, useRef, useState } from "react";
import TopicCard from "./TopicCard";
import TopicService from "./TopicService";
import Logger from "../../../services/Logger";

const FILTER_GENRE_KEY = "admin.topic.filter.genre";
const FILTER_TAGS_KEY = "admin.topic.filter.tags";

const loadFilterGenreId = () => {
    try {
        const stored = localStorage.getItem(FILTER_GENRE_KEY);
        return stored || "";
    } catch (error) {
        Logger.error("Error reading filter genre from localStorage:", error);
        return "";
    }
};

const loadFilterTags = () => {
    try {
        const stored = localStorage.getItem(FILTER_TAGS_KEY);
        if (!stored) return [];
        const parsed = JSON.parse(stored);
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        Logger.error("Error reading filter tags from localStorage:", error);
        return [];
    }
};

const saveFilterGenre = (genreId) => {
    try {
        if (genreId) {
            localStorage.setItem(FILTER_GENRE_KEY, genreId);
        } else {
            localStorage.removeItem(FILTER_GENRE_KEY);
        }
    } catch (error) {
        Logger.error("Error saving filter genre to localStorage:", error);
    }
};

const saveFilterTags = (tags) => {
    try {
        if (tags && Array.isArray(tags) && tags.length > 0) {
            localStorage.setItem(FILTER_TAGS_KEY, JSON.stringify(tags));
        } else {
            localStorage.removeItem(FILTER_TAGS_KEY);
        }
    } catch (error) {
        Logger.error("Error saving filter tags to localStorage:", error);
    }
};

export default function TopicList({ keyword }) {
    const [paging, setPaging] = useState({ page: 0, pageSize: 10 });
    const [sort, setSort] = useState([{ field: "createdAt", sort: "desc" }]);
    const [rowCount, setRowCount] = useState(0);
    const [rows, setRows] = useState([]);
    const [topGenres, setTopGenres] = useState([]);
    const [selectedGenre, setSelectedGenre] = useState(null);
    const [selectedTags, setSelectedTags] = useState(loadFilterTags);
    const [availableTags, setAvailableTags] = useState([]);
    const [tagsLoading, setTagsLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const genreLoadDoneRef = useRef(false);

    useEffect(() => {
        TopicService.requestTopGenres().then((res) => {
            setTopGenres(res);
            const storedGenreId = loadFilterGenreId();
            if (storedGenreId && res && res.length > 0) {
                const genre = res.find((g) => g.id === storedGenreId);
                if (genre) setSelectedGenre(genre);
            }
            genreLoadDoneRef.current = true;
        });
    }, []);

    useEffect(() => {
        setTagsLoading(true);
        TopicService.requestTopicTags()
            .then((tags) => setAvailableTags(Array.isArray(tags) ? tags : []))
            .catch(() => setAvailableTags([]))
            .finally(() => setTagsLoading(false));
    }, []);

    useEffect(() => {
        setLoading(true);
        TopicService.requestList(paging, sort, setRows, setRowCount, keyword, selectedGenre, selectedTags)
            .finally(() => {
                setLoading(false);
            });
    }, [paging, sort, keyword, selectedGenre, selectedTags]);

    useEffect(() => {
        if (genreLoadDoneRef.current) {
            saveFilterGenre(selectedGenre ? selectedGenre.id : "");
        }
    }, [selectedGenre]);

    useEffect(() => {
        saveFilterTags(selectedTags);
    }, [selectedTags]);

    const handlePageChange = (event, value) => {
        setPaging(prev => ({ ...prev, page: value - 1 }));
    };

    const totalPages = Math.ceil(rowCount / paging.pageSize);

    return (
        <Box sx={{ width: "100%" }}>
            <Box sx={{ mb: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "medium" }}>
                        Filter by Genre
                    </Typography>
                    <FormControl component="fieldset">
                        <RadioGroup
                            value={selectedGenre ? selectedGenre.id : ""}
                            onChange={(e) => {
                                const selected = topGenres.find((genre) => genre.id === e.target.value);
                                setSelectedGenre(selected || null);
                                setPaging((prev) => ({ ...prev, page: 0 }));
                            }}
                            row
                        >
                            <FormControlLabel value="" control={<Radio size="small" />} label="All Genres" sx={{ mr: 2 }} />
                            {topGenres.map((genre) => (
                                <FormControlLabel
                                    key={genre.id}
                                    value={genre.id}
                                    control={<Radio size="small" />}
                                    label={genre.name}
                                    sx={{ mr: 2 }}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                </Box>
                <Box sx={{ maxWidth: 400 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "medium" }}>
                        Filter by Tags
                    </Typography>
                    <Autocomplete
                        multiple
                        freeSolo
                        size="small"
                        options={availableTags}
                        value={selectedTags}
                        onChange={(event, newValue) => {
                            const normalizedTags = newValue.map((tag) =>
                                typeof tag === "string" ? tag.trim() : (tag?.tag ?? String(tag)).trim()
                            ).filter((t) => t.length > 0);
                            setSelectedTags(normalizedTags);
                            setPaging((prev) => ({ ...prev, page: 0 }));
                        }}
                        loading={tagsLoading}
                        getOptionLabel={(option) => (typeof option === "string" ? option : option?.tag ?? String(option))}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => {
                                const tagLabel = typeof option === "string" ? option : option?.tag ?? String(option);
                                return <Chip variant="outlined" label={tagLabel} size="small" {...getTagProps({ index })} key={index} />;
                            })
                        }
                        renderInput={(params) => (
                            <TextField {...params} placeholder="Search by tags..." variant="outlined" size="small" />
                        )}
                    />
                </Box>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <CircularProgress />
                </Box>
            ) : rows.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <Typography variant="body1" color="text.secondary">
                        No topics found
                    </Typography>
                </Box>
            ) : (
                <>
                    <Grid container spacing={2}>
                        {rows.map((topic) => (
                            <Grid key={topic.id} size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
                                <TopicCard topic={topic} />
                            </Grid>
                        ))}
                    </Grid>

                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                            <Pagination
                                count={totalPages}
                                page={paging.page + 1}
                                onChange={handlePageChange}
                                color="primary"
                                disabled={loading}
                            />
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
}
